/* eslint-disable jsdoc/require-returns */

/* eslint-disable react/no-array-index-key */
import { useState } from 'react';

import PropTypes from 'prop-types';

import Box from '@zenbusiness/zui/Box';
import Button from '@zenbusiness/zui/Button';
import Text from '@zenbusiness/zui/Text';

import * as Styled from './MarketAnalysisReport.style';

/**
 * MarketAnalysisReport - Displays comprehensive market analysis results
 *
 * Renders all sections from the LLM response including:
 * - Executive Summary
 * - Market Overview
 * - Market Growth Trend
 * - Market Trends
 * - Customer Segmentation
 * - Competitive Landscape
 * - Competitor Profiles
 * - SWOT Analysis
 * - Visual Summary
 * - Actionable Recommendations
 * - ROI Impact Layer
 * - Implementation Guidance
 * - Suggested Next Steps
 * - Methodology
 * @param root0
 * @param root0.data
 * @param root0.actions
 * @param root0.onComponentClick
 */
function MarketAnalysisReport({ data = null, actions = [], onComponentClick = null }) {
  const [loadingAction, setLoadingAction] = useState(null);

  const handleAction = async (actionType) => {
    if (loadingAction) {
      return;
    }
    setLoadingAction(actionType);
    try {
      await onComponentClick?.({ type: actionType, payload: { data } });
    } finally {
      setLoadingAction(null);
    }
  };

  if (!data) {
    return (
      <Styled.ReportRoot>
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Text color="secondary">No report data available</Text>
        </Box>
      </Styled.ReportRoot>
    );
  }

  // Extract all sections
  const {
    executiveSummary = {},
    marketOverview = {},
    marketGrowthTrend = {},
    marketTrends = {},
    customerSegmentation = {},
    competitiveLandscape = {},
    competitorProfiles = {},
    swotAnalysis = {},
    visualSummary = {},
    actionableRecommendations = {},
    roiImpactLayer = {},
    implementationGuidance = {},
    suggestedNextSteps = {},
    methodology = {}
  } = data.marketAnalysis;

  // Extract top-level metadata
  const { id, industry, location, companyName, generatedAt, grounding } = data.marketAnalysis || {};

  return (
    <Styled.ReportRoot>
      {/* ═══════════════════════════════════════════════════════════════════════════ */}
      {/* REPORT HEADER */}
      {/* ═══════════════════════════════════════════════════════════════════════════ */}
      <Styled.ReportHeader>
        <Text size="lg" weight="bold">
          Your Market Analysis Report
        </Text>
        {companyName && <Text size="sm">Company: {companyName}</Text>}
        {industry && <Text size="sm">Industry: {industry}</Text>}
        {location && <Text size="sm">Location: {location}</Text>}
        {generatedAt && <Text size="sm">Generated: {generatedAt}</Text>}
        {id && <Text size="sm">ID: {id}</Text>}
        {grounding && (
          <>
            {grounding.marketDataFound !== undefined && (
              <Text size="sm">Market Data Found: {grounding.marketDataFound.toString()}</Text>
            )}
            {grounding.searchDurationMs !== undefined && (
              <Text size="sm">Search Duration: {grounding.searchDurationMs}ms</Text>
            )}
            {grounding.competitorsSearched !== undefined && (
              <Text size="sm">Competitors Searched: {grounding.competitorsSearched}</Text>
            )}
            {grounding.competitorsVerified !== undefined && (
              <Text size="sm">Competitors Verified: {grounding.competitorsVerified}</Text>
            )}
          </>
        )}
      </Styled.ReportHeader>

      {/* ═══════════════════════════════════════════════════════════════════════════ */}
      {/* 1. EXECUTIVE SUMMARY */}
      {/* ═══════════════════════════════════════════════════════════════════════════ */}
      {executiveSummary.overview && (
        <Styled.Section>
          <Styled.SectionTitle>
            <Text size="md" weight="medium">
              Executive Summary
            </Text>
          </Styled.SectionTitle>
          <Styled.SectionContent>
            <Text>{executiveSummary.overview}</Text>

            {/* Key Metrics */}
            {executiveSummary.keyMetrics && (
              <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                <Text size="sm" weight="medium">
                  Key Metrics:
                </Text>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 1 }}>
                  {executiveSummary.keyMetrics.marketSize && (
                    <Box>
                      <Text color="secondary" size="sm">
                        Market Size
                      </Text>
                      <Text weight="bold">{executiveSummary.keyMetrics.marketSize}</Text>
                    </Box>
                  )}
                  {executiveSummary.keyMetrics.annualGrowthRate && (
                    <Box>
                      <Text color="secondary" size="sm">
                        Growth Rate
                      </Text>
                      <Text weight="bold">{executiveSummary.keyMetrics.annualGrowthRate}</Text>
                    </Box>
                  )}
                </Box>
                {executiveSummary.keyMetrics.primarySegments && (
                  <Box sx={{ mt: 1 }}>
                    <Text color="secondary" size="sm">
                      Primary Segments:{' '}
                    </Text>
                    <Text size="sm">{executiveSummary.keyMetrics.primarySegments.join(', ')}</Text>
                  </Box>
                )}
                {executiveSummary.keyMetrics.topCompetitors && (
                  <Box sx={{ mt: 1 }}>
                    <Text color="secondary" size="sm">
                      Top Competitors:{' '}
                    </Text>
                    <Text size="sm">{executiveSummary.keyMetrics.topCompetitors.join(', ')}</Text>
                  </Box>
                )}
              </Box>
            )}

            {/* Key Highlights */}
            {executiveSummary.keyHighlights && (
              <Box sx={{ mt: 2 }}>
                {executiveSummary.keyHighlights.topOpportunity && (
                  <Box sx={{ p: 2, bgcolor: 'secondary.light', borderRadius: 1, mb: 1 }}>
                    <Text size="sm" weight="medium">
                      Top Opportunity: {executiveSummary.keyHighlights.topOpportunity.title}
                    </Text>
                    <Text size="sm">
                      {executiveSummary.keyHighlights.topOpportunity.description}
                    </Text>
                  </Box>
                )}
                {executiveSummary.keyHighlights.keyChallenge && (
                  <Box sx={{ p: 2, bgcolor: 'warning.light', borderRadius: 1 }}>
                    <Text size="sm" weight="medium">
                      Key Challenge: {executiveSummary.keyHighlights.keyChallenge.title}
                    </Text>
                    <Text size="sm">{executiveSummary.keyHighlights.keyChallenge.description}</Text>
                  </Box>
                )}
              </Box>
            )}

            {/* Key Findings */}
            {executiveSummary.keyFindings?.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Text size="sm" weight="medium">
                  Key Findings:
                </Text>
                <Styled.List>
                  {executiveSummary.keyFindings.map((finding, idx) => (
                    <Styled.ListItem key={idx}>
                      <Text size="sm">{finding}</Text>
                    </Styled.ListItem>
                  ))}
                </Styled.List>
              </Box>
            )}

            {/* Strategic Focus */}
            {executiveSummary.strategicFocus && (
              <Box
                sx={{
                  mt: 2,
                  p: 2,
                  border: '1px solid',
                  borderColor: 'primary.main',
                  borderRadius: 1
                }}
              >
                <Text size="sm" weight="medium">
                  Strategic Focus:
                </Text>
                <Text size="sm">{executiveSummary.strategicFocus}</Text>
              </Box>
            )}

            {/* Confidence */}
            {executiveSummary.confidenceLevel && (
              <Box sx={{ mt: 2 }}>
                <Text color="secondary" size="sm">
                  Confidence: {executiveSummary.confidenceLevel} -{' '}
                  {executiveSummary.confidenceExplanation}
                </Text>
              </Box>
            )}
          </Styled.SectionContent>
        </Styled.Section>
      )}

      {/* ═══════════════════════════════════════════════════════════════════════════ */}
      {/* 2. MARKET OVERVIEW */}
      {/* ═══════════════════════════════════════════════════════════════════════════ */}
      {marketOverview.overview && (
        <Styled.Section>
          <Styled.SectionTitle>
            <Text size="md" weight="medium">
              Market Overview
            </Text>
          </Styled.SectionTitle>
          <Styled.SectionContent>
            <Text>{marketOverview.overview}</Text>
            <Box sx={{ mt: 2, display: 'flex', gap: 4 }}>
              {marketOverview.marketSizeDisplay && (
                <Box>
                  <Text color="secondary" size="sm">
                    {marketOverview.marketSizeLabel || 'Market Size'}
                  </Text>
                  <Text size="lg" weight="bold">
                    {marketOverview.marketSizeDisplay}
                  </Text>
                </Box>
              )}
              {!marketOverview.marketSizeDisplay && marketOverview.marketSize !== undefined && (
                <Box>
                  <Text color="secondary" size="sm">
                    {marketOverview.marketSizeLabel || 'Market Size'}
                  </Text>
                  <Text size="lg" weight="bold">
                    {marketOverview.marketSize}
                  </Text>
                </Box>
              )}
              {marketOverview.annualGrowthRateDisplay && (
                <Box>
                  <Text color="secondary" size="sm">
                    {marketOverview.annualGrowthRateLabel || 'Annual Growth'}
                  </Text>
                  <Text size="lg" weight="bold">
                    {marketOverview.annualGrowthRateDisplay}
                  </Text>
                </Box>
              )}
              {!marketOverview.annualGrowthRateDisplay &&
                marketOverview.annualGrowthRate !== undefined && (
                  <Box>
                    <Text color="secondary" size="sm">
                      {marketOverview.annualGrowthRateLabel || 'Annual Growth'}
                    </Text>
                    <Text size="lg" weight="bold">
                      {marketOverview.annualGrowthRate}
                    </Text>
                  </Box>
                )}
            </Box>
          </Styled.SectionContent>
        </Styled.Section>
      )}

      {/* ═══════════════════════════════════════════════════════════════════════════ */}
      {/* 3. MARKET GROWTH TREND */}
      {/* ═══════════════════════════════════════════════════════════════════════════ */}
      {marketGrowthTrend.overview && (
        <Styled.Section>
          <Styled.SectionTitle>
            <Text size="md" weight="medium">
              Market Growth Trend
            </Text>
          </Styled.SectionTitle>
          <Styled.SectionContent>
            <Text>{marketGrowthTrend.overview}</Text>

            {/* Trend Series Table */}
            {marketGrowthTrend.trend?.series && (
              <Box sx={{ mt: 2, overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
                  <thead>
                    <tr style={{ borderBottom: '2px solid #ccc' }}>
                      <th style={{ textAlign: 'left', padding: '8px' }}>Year</th>
                      <th style={{ textAlign: 'right', padding: '8px' }}>Revenue</th>
                      <th style={{ textAlign: 'right', padding: '8px' }}>Index</th>
                      <th style={{ textAlign: 'center', padding: '8px' }}>Type</th>
                    </tr>
                  </thead>
                  <tbody>
                    {marketGrowthTrend.trend.series.map((item, idx) => (
                      <tr key={idx} style={{ borderBottom: '1px solid #eee' }}>
                        <td style={{ padding: '8px' }}>{item.year}</td>
                        <td style={{ textAlign: 'right', padding: '8px' }}>
                          ${(item.revenue / 1000000).toFixed(1)}M
                        </td>
                        <td style={{ textAlign: 'right', padding: '8px' }}>{item.index}</td>
                        <td style={{ textAlign: 'center', padding: '8px' }}>
                          {item.isProjected ? '📊 Projected' : '✓ Actual'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Box>
            )}

            {/* Metrics */}
            {marketGrowthTrend.metrics && (
              <Box sx={{ mt: 2, display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                {marketGrowthTrend.metrics.cagrDisplay && (
                  <Box>
                    <Text color="secondary" size="sm">
                      CAGR:
                    </Text>{' '}
                    <Text weight="bold">{marketGrowthTrend.metrics.cagrDisplay}</Text>
                  </Box>
                )}
                {!marketGrowthTrend.metrics.cagrDisplay &&
                  marketGrowthTrend.metrics.cagr !== undefined && (
                    <Box>
                      <Text color="secondary" size="sm">
                        CAGR:
                      </Text>{' '}
                      <Text weight="bold">{marketGrowthTrend.metrics.cagr}</Text>
                    </Box>
                  )}
                {marketGrowthTrend.metrics.latestRevenueDisplay && (
                  <Box>
                    <Text color="secondary" size="sm">
                      Latest Revenue:
                    </Text>{' '}
                    <Text weight="bold">{marketGrowthTrend.metrics.latestRevenueDisplay}</Text>
                  </Box>
                )}
                {!marketGrowthTrend.metrics.latestRevenueDisplay &&
                  marketGrowthTrend.metrics.latestRevenue !== undefined && (
                    <Box>
                      <Text color="secondary" size="sm">
                        Latest Revenue:
                      </Text>{' '}
                      <Text weight="bold">{marketGrowthTrend.metrics.latestRevenue}</Text>
                    </Box>
                  )}
                {marketGrowthTrend.metrics.annualGrowthRate !== undefined && (
                  <Box>
                    <Text color="secondary" size="sm">
                      Annual Growth Rate:
                    </Text>{' '}
                    <Text weight="bold">{marketGrowthTrend.metrics.annualGrowthRate}</Text>
                  </Box>
                )}
              </Box>
            )}
            {marketGrowthTrend.trend?.indexBaseYear !== undefined && (
              <Text size="sm">Index Base Year: {marketGrowthTrend.trend.indexBaseYear}</Text>
            )}
            {marketGrowthTrend.trend?.indexBaseValue !== undefined && (
              <Text size="sm">Index Base Value: {marketGrowthTrend.trend.indexBaseValue}</Text>
            )}

            {marketGrowthTrend.insight && (
              <Box sx={{ mt: 2, p: 2, bgcolor: 'info.light', borderRadius: 1 }}>
                <Text size="sm">
                  <strong>Insight:</strong> {marketGrowthTrend.insight}
                </Text>
              </Box>
            )}
          </Styled.SectionContent>
        </Styled.Section>
      )}

      {/* ═══════════════════════════════════════════════════════════════════════════ */}
      {/* 4. MARKET TRENDS */}
      {/* ═══════════════════════════════════════════════════════════════════════════ */}
      {marketTrends.trends?.length > 0 && (
        <Styled.Section>
          <Styled.SectionTitle>
            <Text size="md" weight="medium">
              🔥 Market Trends
            </Text>
          </Styled.SectionTitle>
          <Styled.SectionContent>
            {marketTrends.overview && <Text sx={{ mb: 2 }}>{marketTrends.overview}</Text>}
            {marketTrends.trends.map((trend, idx) => (
              <Box key={idx} sx={{ mb: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                <Box
                  sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                >
                  <Text weight="medium">{trend.title}</Text>
                  {trend.category && (
                    <Text color="secondary" size="sm">
                      {trend.category}
                    </Text>
                  )}
                </Box>
                <Text size="sm" sx={{ mt: 1 }}>
                  {trend.description}
                </Text>
              </Box>
            ))}
          </Styled.SectionContent>
        </Styled.Section>
      )}

      {/* ═══════════════════════════════════════════════════════════════════════════ */}
      {/* 5. CUSTOMER SEGMENTATION */}
      {/* ═══════════════════════════════════════════════════════════════════════════ */}
      {customerSegmentation.segments?.length > 0 && (
        <Styled.Section>
          <Styled.SectionTitle>
            <Text size="md" weight="medium">
              👥 Customer Segmentation
            </Text>
          </Styled.SectionTitle>
          <Styled.SectionContent>
            {customerSegmentation.overview && (
              <Text sx={{ mb: 2 }}>{customerSegmentation.overview}</Text>
            )}

            {customerSegmentation.segments.map((segment, idx) => (
              <Box key={idx} sx={{ mb: 2, p: 2, border: '1px solid #ddd', borderRadius: 1 }}>
                <Box
                  sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                >
                  <Text weight="medium">{segment.segmentName}</Text>
                  <Text color="primary" weight="bold">
                    {segment.percentage}%
                  </Text>
                </Box>
                {segment.ageRange && (
                  <Text color="secondary" size="sm">
                    Age: {segment.ageRange}
                  </Text>
                )}
                <Text size="sm" sx={{ mt: 1 }}>
                  {segment.description}
                </Text>
                {segment.keyNeeds?.length > 0 && (
                  <Box sx={{ mt: 1 }}>
                    <Text color="secondary" size="sm">
                      Key Needs: {segment.keyNeeds.join(' • ')}
                    </Text>
                  </Box>
                )}
              </Box>
            ))}

            {/* Age Distribution */}
            {customerSegmentation.ageDistribution?.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Text size="sm" weight="medium">
                  Age Distribution:
                </Text>
                <Box sx={{ display: 'flex', gap: 2, mt: 1, flexWrap: 'wrap' }}>
                  {customerSegmentation.ageDistribution.map((age, idx) => (
                    <Box
                      key={idx}
                      sx={{ textAlign: 'center', p: 1, bgcolor: 'grey.100', borderRadius: 1 }}
                    >
                      <Text size="sm">{age.group}</Text>
                      <Text weight="bold">{age.percentage}%</Text>
                    </Box>
                  ))}
                </Box>
              </Box>
            )}

            {customerSegmentation.insight && (
              <Box sx={{ mt: 2, p: 2, bgcolor: 'info.light', borderRadius: 1 }}>
                <Text size="sm">
                  <strong>Insight:</strong> {customerSegmentation.insight}
                </Text>
              </Box>
            )}
          </Styled.SectionContent>
        </Styled.Section>
      )}

      {/* ═══════════════════════════════════════════════════════════════════════════ */}
      {/* 6. COMPETITIVE LANDSCAPE */}
      {/* ═══════════════════════════════════════════════════════════════════════════ */}
      {competitiveLandscape.competitors?.length > 0 && (
        <Styled.Section>
          <Styled.SectionTitle>
            <Text size="md" weight="medium">
              🏢 Competitive Landscape
            </Text>
          </Styled.SectionTitle>
          <Styled.SectionContent>
            {competitiveLandscape.overview && (
              <Text sx={{ mb: 2 }}>{competitiveLandscape.overview}</Text>
            )}

            {competitiveLandscape.competitors.map((comp, idx) => (
              <Styled.CompetitorCard key={idx}>
                <Box
                  sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                >
                  <Text weight="medium">{comp.name}</Text>
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    {comp.marketShareDisplay && (
                      <Text color="secondary" size="sm">
                        Share: {comp.marketShareDisplay}
                      </Text>
                    )}
                    {!comp.marketShareDisplay && comp.marketShare !== undefined && (
                      <Text color="secondary" size="sm">
                        Share: {comp.marketShare}
                      </Text>
                    )}
                    {comp.priceLevel && (
                      <Text color="secondary" size="sm">
                        {comp.priceLevel}
                      </Text>
                    )}
                  </Box>
                </Box>
                {comp.verified !== undefined && (
                  <Text size="sm">Verified: {comp.verified.toString()}</Text>
                )}
                {comp.marketPresence && (
                  <Text size="sm">Market Presence: {comp.marketPresence}</Text>
                )}
                {comp.positioning && (
                  <Text color="secondary" size="sm" sx={{ mt: 1 }}>
                    {comp.positioning}
                  </Text>
                )}
                {comp.qualityRating && (
                  <Text size="sm">
                    Quality: {comp.qualityRating} | Satisfaction: {comp.customerSatisfaction}
                  </Text>
                )}
                {comp.strengths?.length > 0 && (
                  <Box sx={{ mt: 1 }}>
                    <Text size="sm">✓ Strengths: {comp.strengths.join(', ')}</Text>
                  </Box>
                )}
                {comp.weaknesses?.length > 0 && (
                  <Box sx={{ mt: 0.5 }}>
                    <Text size="sm">✗ Weaknesses: {comp.weaknesses.join(', ')}</Text>
                  </Box>
                )}
              </Styled.CompetitorCard>
            ))}

            {competitiveLandscape.keyInsights?.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Text size="sm" weight="medium">
                  Key Insights:
                </Text>
                <Styled.List>
                  {competitiveLandscape.keyInsights.map((insight, idx) => (
                    <Styled.ListItem key={idx}>
                      <Text size="sm">{insight}</Text>
                    </Styled.ListItem>
                  ))}
                </Styled.List>
              </Box>
            )}
          </Styled.SectionContent>
        </Styled.Section>
      )}

      {/* ═══════════════════════════════════════════════════════════════════════════ */}
      {/* 7. COMPETITOR PROFILES (Detailed) */}
      {/* ═══════════════════════════════════════════════════════════════════════════ */}
      {competitorProfiles.profiles?.length > 0 && (
        <Styled.Section>
          <Styled.SectionTitle>
            <Text size="md" weight="medium">
              🔍 Detailed Competitor Profiles
            </Text>
          </Styled.SectionTitle>
          <Styled.SectionContent>
            {competitorProfiles.overview && (
              <Text sx={{ mb: 2 }}>{competitorProfiles.overview}</Text>
            )}

            {competitorProfiles.profiles.map((profile, idx) => (
              <Box key={idx} sx={{ mb: 2, p: 2, border: '1px solid #ddd', borderRadius: 1 }}>
                <Box
                  sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                >
                  <Text weight="medium">{profile.name}</Text>
                  <Text color="secondary" size="sm">
                    {profile.pricePoint} | Est. {profile.established}
                  </Text>
                </Box>
                <Text size="sm" sx={{ mt: 1 }}>
                  Market Share: {profile.marketShareDisplay || profile.marketShare} | Locations:{' '}
                  {profile.locations}
                </Text>
                {profile.verified !== undefined && (
                  <Text size="sm">Verified: {profile.verified.toString()}</Text>
                )}
                {profile.confidence !== undefined && (
                  <Text size="sm">Confidence: {profile.confidence}</Text>
                )}
                <Text size="sm" sx={{ mt: 1 }}>
                  <strong>Target:</strong> {profile.targetMarket}
                </Text>
                <Text size="sm">
                  <strong>Value Prop:</strong> {profile.valueProposition}
                </Text>
                {profile.strengths?.length > 0 && (
                  <Text size="sm" sx={{ mt: 1 }}>
                    ✓ {profile.strengths.join(' • ')}
                  </Text>
                )}
                {profile.weaknesses?.length > 0 && (
                  <Text size="sm">✗ {profile.weaknesses.join(' • ')}</Text>
                )}
              </Box>
            ))}

            {competitorProfiles.insights?.length > 0 && (
              <Box sx={{ mt: 2, p: 2, bgcolor: 'info.light', borderRadius: 1 }}>
                <Text size="sm" weight="medium">
                  Insights:
                </Text>
                {competitorProfiles.insights.map((insight, idx) => (
                  <Text key={idx} size="sm" sx={{ mt: 0.5 }}>
                    • {insight}
                  </Text>
                ))}
              </Box>
            )}
          </Styled.SectionContent>
        </Styled.Section>
      )}

      {/* ═══════════════════════════════════════════════════════════════════════════ */}
      {/* 8. SWOT ANALYSIS */}
      {/* ═══════════════════════════════════════════════════════════════════════════ */}
      {(swotAnalysis.strengths ||
        swotAnalysis.weaknesses ||
        swotAnalysis.opportunities ||
        swotAnalysis.threats) && (
        <Styled.Section>
          <Styled.SectionTitle>
            <Text size="md" weight="medium">
              🎯 SWOT Analysis
            </Text>
          </Styled.SectionTitle>
          <Styled.SectionContent>
            {swotAnalysis.overview && <Text sx={{ mb: 2 }}>{swotAnalysis.overview}</Text>}

            <Box
              sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}
            >
              {/* Strengths */}
              {swotAnalysis.strengths?.length > 0 && (
                <Box sx={{ p: 2, bgcolor: 'success.light', borderRadius: 1 }}>
                  <Text weight="medium">💪 Strengths</Text>
                  {swotAnalysis.strengths.map((s, idx) => (
                    <Text key={idx} size="sm">
                      • {s}
                    </Text>
                  ))}
                </Box>
              )}
              {/* Weaknesses */}
              {swotAnalysis.weaknesses?.length > 0 && (
                <Box sx={{ p: 2, bgcolor: 'error.light', borderRadius: 1 }}>
                  <Text weight="medium">⚡ Weaknesses</Text>
                  {swotAnalysis.weaknesses.map((w, idx) => (
                    <Text key={idx} size="sm">
                      • {w}
                    </Text>
                  ))}
                </Box>
              )}
              {/* Opportunities */}
              {swotAnalysis.opportunities?.length > 0 && (
                <Box sx={{ p: 2, bgcolor: 'info.light', borderRadius: 1 }}>
                  <Text weight="medium">💡 Opportunities</Text>
                  {swotAnalysis.opportunities.map((o, idx) => (
                    <Text key={idx} size="sm">
                      • {o}
                    </Text>
                  ))}
                </Box>
              )}
              {/* Threats */}
              {swotAnalysis.threats?.length > 0 && (
                <Box sx={{ p: 2, bgcolor: 'warning.light', borderRadius: 1 }}>
                  <Text weight="medium">⚠️ Threats</Text>
                  {swotAnalysis.threats.map((t, idx) => (
                    <Text key={idx} size="sm">
                      • {t}
                    </Text>
                  ))}
                </Box>
              )}
            </Box>

            {/* Strategic Implications */}
            {swotAnalysis.strategicImplications && (
              <Box sx={{ mt: 2, p: 2, border: '1px solid #ddd', borderRadius: 1 }}>
                <Text size="sm" weight="medium">
                  Strategic Implications:
                </Text>
                {swotAnalysis.strategicImplications.leverageStrengths && (
                  <Text size="sm" sx={{ mt: 1 }}>
                    • <strong>Leverage:</strong>{' '}
                    {swotAnalysis.strategicImplications.leverageStrengths}
                  </Text>
                )}
                {swotAnalysis.strategicImplications.addressWeaknesses && (
                  <Text size="sm">
                    • <strong>Address:</strong>{' '}
                    {swotAnalysis.strategicImplications.addressWeaknesses}
                  </Text>
                )}
                {swotAnalysis.strategicImplications.seizeOpportunities && (
                  <Text size="sm">
                    • <strong>Seize:</strong>{' '}
                    {swotAnalysis.strategicImplications.seizeOpportunities}
                  </Text>
                )}
                {swotAnalysis.strategicImplications.mitigateThreats && (
                  <Text size="sm">
                    • <strong>Mitigate:</strong>{' '}
                    {swotAnalysis.strategicImplications.mitigateThreats}
                  </Text>
                )}
              </Box>
            )}
          </Styled.SectionContent>
        </Styled.Section>
      )}

      {/* ═══════════════════════════════════════════════════════════════════════════ */}
      {/* 9. VISUAL SUMMARY (Data for Charts) */}
      {/* ═══════════════════════════════════════════════════════════════════════════ */}
      {(visualSummary.bubbleChart || visualSummary.radar || visualSummary.priceMix) && (
        <Styled.Section>
          <Styled.SectionTitle>
            <Text size="md" weight="medium">
              📊 Visual Summary Data
            </Text>
          </Styled.SectionTitle>
          <Styled.SectionContent>
            {visualSummary.notes && (
              <Text color="secondary" size="sm" sx={{ mb: 2 }}>
                {visualSummary.notes}
              </Text>
            )}

            {/* Bubble Chart Data */}
            {visualSummary.bubbleChart?.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Text size="sm" weight="medium">
                  Competitive Position (Bubble Chart Data):
                </Text>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 1 }}>
                  {visualSummary.bubbleChart.map((item, idx) => (
                    <Box
                      key={idx}
                      sx={{ p: 1, bgcolor: 'grey.100', borderRadius: 1, textAlign: 'center' }}
                    >
                      <Text size="sm" weight="medium">
                        {item.name}
                      </Text>
                      <Text size="sm">
                        Price: {item.x} | Quality: {item.y} | Share: {item.size}%
                      </Text>
                      {item.colorKey && <Text size="sm">Color Key: {item.colorKey}</Text>}
                    </Box>
                  ))}
                </Box>
              </Box>
            )}
            {visualSummary.legend && (
              <Box sx={{ mb: 2 }}>
                <Text size="sm" weight="medium">
                  Legend:
                </Text>
                {visualSummary.legend.colorLegend && (
                  <>
                    {visualSummary.legend.colorLegend.budget && (
                      <Text size="sm">Budget Color: {visualSummary.legend.colorLegend.budget}</Text>
                    )}
                    {visualSummary.legend.colorLegend.mid && (
                      <Text size="sm">Mid Color: {visualSummary.legend.colorLegend.mid}</Text>
                    )}
                    {visualSummary.legend.colorLegend.premium && (
                      <Text size="sm">
                        Premium Color: {visualSummary.legend.colorLegend.premium}
                      </Text>
                    )}
                  </>
                )}
                {visualSummary.legend.xAxisLabels && (
                  <>
                    {visualSummary.legend.xAxisLabels['1'] && (
                      <Text size="sm">X-Axis Label 1: {visualSummary.legend.xAxisLabels['1']}</Text>
                    )}
                    {visualSummary.legend.xAxisLabels['2'] && (
                      <Text size="sm">X-Axis Label 2: {visualSummary.legend.xAxisLabels['2']}</Text>
                    )}
                    {visualSummary.legend.xAxisLabels['3'] && (
                      <Text size="sm">X-Axis Label 3: {visualSummary.legend.xAxisLabels['3']}</Text>
                    )}
                  </>
                )}
              </Box>
            )}

            {/* Radar Chart Data */}
            {visualSummary.radar?.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Text size="sm" weight="medium">
                  Competitor Scores (Radar Chart Data):
                </Text>
                <table
                  style={{
                    width: '100%',
                    borderCollapse: 'collapse',
                    fontSize: '0.75rem',
                    marginTop: '8px'
                  }}
                >
                  <thead>
                    <tr style={{ borderBottom: '2px solid #ccc' }}>
                      <th style={{ textAlign: 'left', padding: '4px' }}>Name</th>
                      <th style={{ textAlign: 'center', padding: '4px' }}>Brand</th>
                      <th style={{ textAlign: 'center', padding: '4px' }}>Innovation</th>
                      <th style={{ textAlign: 'center', padding: '4px' }}>Service</th>
                      <th style={{ textAlign: 'center', padding: '4px' }}>Digital</th>
                      <th style={{ textAlign: 'center', padding: '4px' }}>Value</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visualSummary.radar.map((item, idx) => (
                      <tr key={idx} style={{ borderBottom: '1px solid #eee' }}>
                        <td style={{ padding: '4px' }}>{item.name}</td>
                        <td style={{ textAlign: 'center', padding: '4px' }}>
                          {item.brandStrength}
                        </td>
                        <td style={{ textAlign: 'center', padding: '4px' }}>
                          {item.innovationScore}
                        </td>
                        <td style={{ textAlign: 'center', padding: '4px' }}>{item.serviceScore}</td>
                        <td style={{ textAlign: 'center', padding: '4px' }}>
                          {item.digitalSharePct}%
                        </td>
                        <td style={{ textAlign: 'center', padding: '4px' }}>{item.valueScore}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Box>
            )}

            {/* Price Mix */}
            {visualSummary.priceMix?.length > 0 && (
              <Box>
                <Text size="sm" weight="medium">
                  Market Price Mix:
                </Text>
                <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
                  {visualSummary.priceMix.map((item, idx) => (
                    <Box
                      key={idx}
                      sx={{ p: 1, bgcolor: 'grey.100', borderRadius: 1, textAlign: 'center' }}
                    >
                      <Text size="sm">{item.tier}</Text>
                      <Text weight="bold">{item.share}%</Text>
                    </Box>
                  ))}
                </Box>
              </Box>
            )}
          </Styled.SectionContent>
        </Styled.Section>
      )}

      {/* ═══════════════════════════════════════════════════════════════════════════ */}
      {/* 10. ACTIONABLE RECOMMENDATIONS */}
      {/* ═══════════════════════════════════════════════════════════════════════════ */}
      {actionableRecommendations.recommendations?.length > 0 && (
        <Styled.Section>
          <Styled.SectionTitle>
            <Text size="md" weight="medium">
              ✅ Actionable Recommendations
            </Text>
          </Styled.SectionTitle>
          <Styled.SectionContent>
            {actionableRecommendations.overview && (
              <Text sx={{ mb: 2 }}>{actionableRecommendations.overview}</Text>
            )}

            {actionableRecommendations.recommendations.map((rec, idx) => (
              <Styled.RecommendationCard key={idx}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start'
                  }}
                >
                  <Text weight="medium">{rec.title}</Text>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    {rec.priority && (
                      <Styled.PriorityBadge priority={rec.priority}>
                        {rec.priority}
                      </Styled.PriorityBadge>
                    )}
                    {rec.timeframe && (
                      <Text color="secondary" size="sm">
                        {rec.timeframe}
                      </Text>
                    )}
                  </Box>
                </Box>
                <Text size="sm" sx={{ mt: 1 }}>
                  {rec.description}
                </Text>
              </Styled.RecommendationCard>
            ))}

            {actionableRecommendations.implementationGuidance?.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Text size="sm" weight="medium">
                  Implementation Guidance:
                </Text>
                {actionableRecommendations.implementationGuidance.map((g, idx) => (
                  <Text key={idx} size="sm">
                    • {g}
                  </Text>
                ))}
              </Box>
            )}

            {actionableRecommendations.keyTakeaway && (
              <Box sx={{ mt: 2, p: 2, bgcolor: 'secondary.light', borderRadius: 1 }}>
                <Text size="sm">
                  <strong>Key Takeaway:</strong> {actionableRecommendations.keyTakeaway}
                </Text>
              </Box>
            )}
          </Styled.SectionContent>
        </Styled.Section>
      )}

      {/* ═══════════════════════════════════════════════════════════════════════════ */}
      {/* 11. ROI IMPACT LAYER */}
      {/* ═══════════════════════════════════════════════════════════════════════════ */}
      {roiImpactLayer.recommendationsRoi?.length > 0 && (
        <Styled.Section>
          <Styled.SectionTitle>
            <Text size="md" weight="medium">
              💰 ROI Impact Analysis
            </Text>
          </Styled.SectionTitle>
          <Styled.SectionContent>
            {roiImpactLayer.overview && <Text sx={{ mb: 2 }}>{roiImpactLayer.overview}</Text>}

            {/* Summary Metrics */}
            {roiImpactLayer.summaryMetrics && (
              <Box sx={{ mb: 2, display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                <Box sx={{ p: 2, bgcolor: 'success.light', borderRadius: 1, textAlign: 'center' }}>
                  <Text size="sm">Avg ROI</Text>
                  <Text size="lg" weight="bold">
                    {roiImpactLayer.summaryMetrics.averageRoiDisplay ||
                      roiImpactLayer.summaryMetrics.averageRoi}
                  </Text>
                </Box>
                <Box sx={{ p: 2, bgcolor: 'info.light', borderRadius: 1, textAlign: 'center' }}>
                  <Text size="sm">Avg Effort</Text>
                  <Text size="lg" weight="bold">
                    {roiImpactLayer.summaryMetrics.averageEffortDisplay ||
                      roiImpactLayer.summaryMetrics.averageEffort}
                  </Text>
                </Box>
                <Box sx={{ p: 2, bgcolor: 'warning.light', borderRadius: 1, textAlign: 'center' }}>
                  <Text size="sm">ROI/Effort</Text>
                  <Text size="lg" weight="bold">
                    {roiImpactLayer.summaryMetrics.roiToEffortRatioDisplay ||
                      roiImpactLayer.summaryMetrics.roiToEffortRatio}
                  </Text>
                </Box>
              </Box>
            )}

            {/* ROI by Recommendation */}
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #ccc' }}>
                  <th style={{ textAlign: 'left', padding: '8px' }}>Recommendation</th>
                  <th style={{ textAlign: 'center', padding: '8px' }}>ROI</th>
                  <th style={{ textAlign: 'center', padding: '8px' }}>Effort</th>
                  <th style={{ textAlign: 'left', padding: '8px' }}>Category</th>
                </tr>
              </thead>
              <tbody>
                {roiImpactLayer.recommendationsRoi.map((item, idx) => (
                  <tr key={idx} style={{ borderBottom: '1px solid #eee' }}>
                    <td style={{ padding: '8px' }}>{item.title}</td>
                    <td style={{ textAlign: 'center', padding: '8px' }}>
                      {item.expectedRoiDisplay || item.expectedRoi}
                    </td>
                    <td style={{ textAlign: 'center', padding: '8px' }}>
                      {item.effortLevelDisplay || item.effortLevel}
                    </td>
                    <td style={{ padding: '8px' }}>{item.roiCategory}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {roiImpactLayer.recommendationsRoi?.map((item, idx) =>
              item.roiNotes ? (
                <Box key={idx} sx={{ mt: 1 }}>
                  <Text size="sm">
                    {item.title} ROI Notes: {item.roiNotes}
                  </Text>
                </Box>
              ) : null
            )}
          </Styled.SectionContent>
        </Styled.Section>
      )}

      {/* ═══════════════════════════════════════════════════════════════════════════ */}
      {/* 12. IMPLEMENTATION GUIDANCE */}
      {/* ═══════════════════════════════════════════════════════════════════════════ */}
      {implementationGuidance.steps?.length > 0 && (
        <Styled.Section>
          <Styled.SectionTitle>
            <Text size="md" weight="medium">
              🗺️ Implementation Roadmap
            </Text>
          </Styled.SectionTitle>
          <Styled.SectionContent>
            {implementationGuidance.overview && (
              <Text sx={{ mb: 2 }}>{implementationGuidance.overview}</Text>
            )}

            {/* Timeline */}
            {implementationGuidance.timeline && (
              <Box sx={{ mb: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
                <Text weight="medium">Timeline: {implementationGuidance.timeline.duration}</Text>
                <Text size="sm">{implementationGuidance.timeline.description}</Text>
                {implementationGuidance.timeline.durationMonths !== undefined && (
                  <Text size="sm">
                    Duration Months: {implementationGuidance.timeline.durationMonths}
                  </Text>
                )}
              </Box>
            )}
            {implementationGuidance.confidence !== undefined && (
              <Text size="sm">Confidence: {implementationGuidance.confidence}</Text>
            )}

            {/* Steps */}
            {implementationGuidance.steps.map((step, idx) => (
              <Box
                key={idx}
                sx={{
                  mb: 2,
                  p: 2,
                  border: '1px solid #ddd',
                  borderRadius: 1,
                  borderLeft: '4px solid',
                  borderLeftColor: 'primary.main'
                }}
              >
                <Text weight="medium">
                  Step {step.stepNumber}: {step.title}
                </Text>
                <Text size="sm" sx={{ mt: 1 }}>
                  {step.description}
                </Text>
                <Text color="secondary" size="sm" sx={{ mt: 1 }}>
                  <strong>Rationale:</strong> {step.rationale}
                </Text>
                {step.exampleMetrics?.length > 0 && (
                  <Box sx={{ mt: 1 }}>
                    <Text color="secondary" size="sm">
                      Metrics: {step.exampleMetrics.join(' • ')}
                    </Text>
                  </Box>
                )}
              </Box>
            ))}

            {implementationGuidance.notes && (
              <Text color="secondary" size="sm" sx={{ mt: 2 }}>
                <em>Note: {implementationGuidance.notes}</em>
              </Text>
            )}
          </Styled.SectionContent>
        </Styled.Section>
      )}

      {/* ═══════════════════════════════════════════════════════════════════════════ */}
      {/* 13. SUGGESTED NEXT STEPS */}
      {/* ═══════════════════════════════════════════════════════════════════════════ */}
      {suggestedNextSteps.steps?.length > 0 && (
        <Styled.Section>
          <Styled.SectionTitle>
            <Text size="md" weight="medium">
              🚀 Suggested Next Steps (30-60 Days)
            </Text>
          </Styled.SectionTitle>
          <Styled.SectionContent>
            {suggestedNextSteps.overview && (
              <Text sx={{ mb: 2 }}>{suggestedNextSteps.overview}</Text>
            )}

            {suggestedNextSteps.introCallout && (
              <Box sx={{ mb: 2, p: 2, bgcolor: 'success.light', borderRadius: 1 }}>
                <Text size="sm">{suggestedNextSteps.introCallout}</Text>
              </Box>
            )}

            <Styled.List>
              {suggestedNextSteps.steps.map((step, idx) => (
                <Styled.ListItem key={idx}>
                  <Text>{step}</Text>
                </Styled.ListItem>
              ))}
            </Styled.List>

            {/* Timeline Phases */}
            {suggestedNextSteps.timelinePhases?.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Text size="sm" weight="medium">
                  Timeline:
                </Text>
                {suggestedNextSteps.timelinePhases.map((phase, idx) => (
                  <Box key={idx} sx={{ mt: 1, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                    <Text size="sm" weight="medium">
                      {phase.phase}
                    </Text>
                    <Text size="sm">{phase.description}</Text>
                  </Box>
                ))}
              </Box>
            )}

            {suggestedNextSteps.summary && (
              <Box
                sx={{
                  mt: 2,
                  p: 2,
                  border: '1px solid',
                  borderColor: 'primary.main',
                  borderRadius: 1
                }}
              >
                <Text size="sm">
                  <strong>Summary:</strong> {suggestedNextSteps.summary}
                </Text>
              </Box>
            )}
            {suggestedNextSteps.confidence !== undefined && (
              <Text size="sm">Confidence: {suggestedNextSteps.confidence}</Text>
            )}
          </Styled.SectionContent>
        </Styled.Section>
      )}

      {/* ═══════════════════════════════════════════════════════════════════════════ */}
      {/* 14. METHODOLOGY */}
      {/* ═══════════════════════════════════════════════════════════════════════════ */}
      {methodology.dataSources?.length > 0 && (
        <Styled.Section>
          <Styled.SectionTitle>
            <Text size="md" weight="medium">
              Methodology & Data Sources
            </Text>
          </Styled.SectionTitle>
          <Styled.SectionContent>
            {methodology.overview && (
              <Text sx={{ mb: 1 }} weight="medium">
                {methodology.overview}
              </Text>
            )}

            {methodology.introText && (
              <Text size="sm" sx={{ mb: 2 }}>
                {methodology.introText}
              </Text>
            )}

            {methodology.dataSources.map((source, idx) => (
              <Box key={idx} sx={{ mb: 1, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                <Text size="sm" weight="medium">
                  {source.name}
                </Text>
                <Text size="sm">{source.description}</Text>
                <Text size="sm">URL: {source.url}</Text>
                <Text color="secondary" size="sm">
                  Coverage: {source.coverage}
                </Text>
              </Box>
            ))}

            {methodology.confidence && (
              <Text color="secondary" size="sm" sx={{ mt: 2 }}>
                Confidence: {(methodology.confidence * 100).toFixed(0)}%
              </Text>
            )}

            {methodology.notes && (
              <Text color="secondary" size="sm" sx={{ mt: 1 }}>
                <em>Note: {methodology.notes}</em>
              </Text>
            )}
          </Styled.SectionContent>
        </Styled.Section>
      )}

      {/* ═══════════════════════════════════════════════════════════════════════════ */}
      {/* ACTION BUTTONS */}
      {/* ═══════════════════════════════════════════════════════════════════════════ */}
      {actions.length > 0 && (
        <Styled.ActionBar>
          {/* {actions.includes('download_pdf') && (
            <Button
              color="primary"
              disabled={!!loadingAction}
              loading={loadingAction === 'download_pdf'}
              size="medium"
              variant="contained"
              onClick={() => handleAction('download_pdf')}
            >
              Download PDF
            </Button>
          )}
          {actions.includes('generate_more') && (
            <Button
              color="info"
              disabled={!!loadingAction}
              loading={loadingAction === 'generate_more'}
              size="medium"
              variant="outlined"
              onClick={() => handleAction('generate_more')}
            >
              Generate More
            </Button>
          )} */}
          {actions.includes('start_over') && (
            <Button
              disabled={!!loadingAction}
              loading={loadingAction === 'start_over'}
              size="medium"
              variant="outlined"
              onClick={() => handleAction('start_over')}
            >
              Start Over
            </Button>
          )}
        </Styled.ActionBar>
      )}
    </Styled.ReportRoot>
  );
}

MarketAnalysisReport.propTypes = {
  data: PropTypes.object,
  actions: PropTypes.arrayOf(PropTypes.string),
  onComponentClick: PropTypes.func
};

export default MarketAnalysisReport;
